import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import type { SubscriberRow } from './types';
import {
  channelLabel,
  channelsToKeyArray,
  fetchActiveChannels,
  visibilityLabel,
  type ChannelRow,
} from '../../lib/channels';
import { datetimeLocalToIso, isoToDatetimeLocal } from '../../lib/subscriberDates';
import {
  SUBSCRIBER_EMAIL_STATUSES,
  SUBSCRIBER_EMAIL_STATUS_DEFAULT,
  subscriberEmailStatus,
} from '../../lib/subscriberEmailStatus';
import { SUBSCRIBER_FUNNEL_STATUSES } from '../../lib/subscriberFunnelStatus';

async function tryUpdateSubscriber({
  matcher,
  patch,
}: {
  matcher: { column: string; value: string | number };
  patch: Record<string, unknown>;
}) {
  const { error } = await supabase.from('subscribers').update(patch).eq(matcher.column, matcher.value);
  if (error) throw error;
}

export default function SubscriberModal({
  isOpen,
  onClose,
  subscriber,
  initialDraft,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  subscriber: SubscriberRow | null;
  /** Prefill when creating from Calendly import, etc. */
  initialDraft?: Partial<SubscriberRow> | null;
  onSuccess: () => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [emailStatus, setEmailStatus] = useState('');
  const [funnelStatus, setFunnelStatus] = useState('');
  const [source, setSource] = useState('');
  const [lastContactedAt, setLastContactedAt] = useState('');
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [catalog, setCatalog] = useState<ChannelRow[]>([]);
  const [saving, setSaving] = useState(false);

  const matcher = useMemo(() => {
    const id = subscriber?.id;
    if (typeof id === 'string' && id) return { column: 'id', value: id };
    if (typeof id === 'number' && Number.isFinite(id)) return { column: 'id', value: id };
    const e = typeof subscriber?.email === 'string' ? subscriber.email.trim() : '';
    if (e) return { column: 'email', value: e };
    return null;
  }, [subscriber?.id, subscriber?.email]);

  useEffect(() => {
    if (!isOpen) return;
    const base = subscriber ?? initialDraft ?? null;
    setEmail(typeof base?.email === 'string' ? base.email : '');
    setName(typeof base?.name === 'string' ? base.name : '');
    setEmailStatus(
      subscriberEmailStatus(base) ||
        (subscriber ? '' : SUBSCRIBER_EMAIL_STATUS_DEFAULT),
    );
    setFunnelStatus(typeof base?.funnel_status === 'string' ? base.funnel_status : '');
    setSource(typeof base?.source === 'string' ? base.source : '');
    setLastContactedAt(
      isoToDatetimeLocal(
        typeof base?.last_contacted_at === 'string' ? base.last_contacted_at : null,
      ),
    );
    setSelectedChannels(channelsToKeyArray(base?.channels));
  }, [isOpen, subscriber, initialDraft]);

  useEffect(() => {
    if (!isOpen) return;
    void fetchActiveChannels()
      .then(setCatalog)
      .catch(() => toast.error('Failed to load channels'));
  }, [isOpen]);

  const channelsByVisibility = useMemo(() => {
    const groups: Record<string, ChannelRow[]> = { public: [], private: [], internal: [] };
    for (const c of catalog) {
      groups[c.visibility]?.push(c);
    }
    for (const slug of selectedChannels) {
      if (!catalog.some((c) => c.slug === slug)) {
        groups.internal.push({
          id: slug,
          slug,
          label: channelLabel(slug),
          description: null,
          visibility: 'internal',
          sort_order: 999,
          active: true,
          created_at: '',
          updated_at: '',
        });
      }
    }
    return groups;
  }, [catalog, selectedChannels]);

  const toggleChannel = (slug: string) => {
    setSelectedChannels((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleSave = async () => {
    const emailTrim = email.trim();
    if (!emailTrim || !emailTrim.includes('@')) {
      toast.error('Valid email is required');
      return;
    }

    setSaving(true);
    try {
      const channelsMap: Record<string, boolean> = {};
      for (const k of selectedChannels) channelsMap[k] = true;

      const emailStatusTrim = emailStatus.trim();
      const basePatch: Record<string, unknown> = {
        email: emailTrim,
        name: name.trim() || null,
        funnel_status: funnelStatus.trim() || null,
        source: source.trim() || null,
        last_contacted_at: datetimeLocalToIso(lastContactedAt),
        updated_at: new Date().toISOString(),
      };

      if (subscriber && matcher) {
        if (emailStatusTrim) {
          basePatch.email_status = emailStatusTrim;
        }
        await tryUpdateSubscriber({
          matcher,
          patch: { ...basePatch, channels: channelsMap },
        });
        toast.success('Subscriber updated');
      } else {
        const insertPatch: Record<string, unknown> = {
          ...basePatch,
          email_status: emailStatusTrim || SUBSCRIBER_EMAIL_STATUS_DEFAULT,
          channels: channelsMap,
          created_at: new Date().toISOString(),
        };
        if (initialDraft?.custom_data && typeof initialDraft.custom_data === 'object') {
          insertPatch.custom_data = initialDraft.custom_data;
        }
        if (typeof initialDraft?.role === 'string' && initialDraft.role.trim()) {
          insertPatch.role = initialDraft.role.trim();
        }
        const { error } = await supabase.from('subscribers').insert(insertPatch);
        if (error) throw error;
        toast.success('Subscriber created');
      }

      onSuccess();
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 shadow-xl w-full max-w-xl flex flex-col min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscriber-modal-title"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 id="subscriber-modal-title" className="text-lg font-semibold text-gray-900 dark:text-white">
            {subscriber ? 'Edit subscriber' : 'New subscriber'}
          </h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email status
              </label>
              <select
                value={emailStatus}
                onChange={(e) => setEmailStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">Not set</option>
                {SUBSCRIBER_EMAIL_STATUSES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
                {emailStatus &&
                !SUBSCRIBER_EMAIL_STATUSES.some((item) => item.value === emailStatus) ? (
                  <option value={emailStatus}>{emailStatus} (current)</option>
                ) : null}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Source</label>
              <input
                type="text"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="e.g. calendly"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Funnel status
              </label>
              <select
                value={funnelStatus}
                onChange={(e) => setFunnelStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">Not set</option>
                {SUBSCRIBER_FUNNEL_STATUSES.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Last connected
              </label>
              <input
                type="datetime-local"
                value={lastContactedAt}
                onChange={(e) => setLastContactedAt(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Channels</label>
            {(['public', 'private', 'internal'] as const).map((vis) => {
              const items = channelsByVisibility[vis];
              if (!items?.length) return null;
              return (
                <div key={vis} className="mb-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
                    {visibilityLabel(vis)}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {items.map((ch) => {
                      const active = selectedChannels.includes(ch.slug);
                      return (
                        <button
                          key={ch.slug}
                          type="button"
                          onClick={() => toggleChannel(ch.slug)}
                          aria-pressed={active}
                          className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                            active
                              ? 'bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600'
                              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                          title={ch.description ?? ch.slug}
                        >
                          {ch.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              Manage channel definitions on the Channels page. Internal channels are for dry-run sends.
            </p>
          </div>
        </div>

        <div className="shrink-0 flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => void handleSave()}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 rounded-lg transition-colors"
          >
            {saving ? 'Saving…' : subscriber ? 'Save changes' : 'Create subscriber'}
          </button>
        </div>
      </div>
    </div>
  );
}
